/**
 * Generic lookup helpers for UmlCanvasEngine's shapes/connectors arrays.
 * Pure functions, no engine state.
 */

/**
 * Finds an item by id in a flat list (shapes or connectors).
 * @param {Array} list
 * @param {string} id
 * @returns {object|undefined}
 */
export function findById(list, id) {
    return list.find(function matchId(item) {
        return item.id === id;
    });
}

/**
 * Finds the highest zIndex across shapes, including nested children.
 * @param {Array} shapes
 * @returns {number}
 */
export function findMaxZIndex(shapes) {
    let max = 0;
    shapes.forEach(function checkShape(shape) {
        max = Math.max(max, shape.zIndex || 0);
        if (shape.children) {
            max = Math.max(max, findMaxZIndex(shape.children));
        }
    });
    return max;
}

/**
 * Finds a shape by id, recursing into children (package contents).
 * @param {Array} shapes
 * @param {string} shapeId
 * @returns {object|null}
 */
export function findNestedShape(shapes, shapeId) {
    for (let i = 0; i < shapes.length; i += 1) {
        const shape = shapes[i];
        if (shape.id === shapeId) {
            return shape;
        }
        if (shape.children) {
            const found = findNestedShape(shape.children, shapeId);
            if (found) {
                return found;
            }
        }
    }
    return null;
}

/**
 * Collects a single shape's own id plus every nested child id (recursive).
 *
 * WHY THIS EXISTS: a package's children live INSIDE the shape object
 * (shape.children), but connectors never do — they live in
 * UmlConnectorEngine.connectors as a flat list and only reference a shape by
 * id. Any code that needs to fully account for "this shape and everything it
 * contains" (delete-cascade cleanup, undo snapshots) needs the full id list,
 * not just shape.id, or it silently misses connectors attached to nested
 * children.
 * @param {object} shape - a single top-level or nested UmlShape instance
 * @returns {string[]} shape.id followed by every descendant id
 */
export function collectShapeIdsDeep(shape) {
    const ids = [shape.id];
    if (shape.children) {
        shape.children.forEach(function collectChildIds(child) {
            ids.push(...collectShapeIdsDeep(child));
        });
    }
    return ids;
}
